import type { OnboardingEmailTemplate } from '../OnboardingEmailModal.types';
import { documentItem, questionItem } from './templateItems';

const ENGAGEMENT_DETAILS = 'Engagement Details';
const COMPANY_DOCUMENTS = 'Company Documents/Information';
const TRANSACTION_STRUCTURE = 'Hypothetical Transaction, Cost of Debt & Shares Outstanding';

// Based on the "ASC 350 Info" tab of the "ASC 350 and ASC 360 - Information Request List" workbook.
export const ASC_350_IMPAIRMENT_TEMPLATE: OnboardingEmailTemplate = {
  id: 'asc-350-impairment',
  label: 'Goodwill Impairment (ASC 350)',
  emailSubject: 'Information request for your goodwill impairment analysis',
  valuationTypes: ['Goodwill Impairment (ASC 350)'],
  requestValuationType: 'asc_350_impairment',
  introduction: `Hello {{CONTACT_FIRST_NAME}},

Thank you for partnering with us. We are ready to move forward with the ASC 350 goodwill and indefinite-lived asset impairment analysis.

To help us build an accurate model and keep things moving efficiently, please reply to this email with your responses to the questions below and upload the requested documents to the portal. Where available, Excel versions are strongly preferred to ensure precision in our analysis.`,
  documents: [
    questionItem(ENGAGEMENT_DETAILS, 'Company name', 'Company Name'),
    questionItem(ENGAGEMENT_DETAILS, 'Measurement date', 'Measurement Date'),
    questionItem(ENGAGEMENT_DETAILS, 'Auditor', 'Auditor'),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Triggering event',
      'Triggering Event - Please describe any issues that could result in goodwill/indefinite-lived asset impairment for each Reporting Unit'
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'PCC election for goodwill?',
      'Did the Company make the PCC election for goodwill?',
      {
        description:
          'private companies only; if so, please confirm if the goodwill impairment analysis is tested at the Reporting Unit or Entity level',
      }
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Reporting units / entities',
      'Number and name of each Reporting Unit (or Entity, if testing at the Entity level)'
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Indefinite-lived intangibles?',
      'Do any Reporting Units/Entities have indefinite-lived intangible assets on their balance sheet that require impairment testing as of the Measurement Date?',
      { description: 'if so, we will have follow-up questions in a separate data request' }
    ),

    documentItem(
      COMPANY_DOCUMENTS,
      'Business plan / investment presentation',
      'Business plan or investment presentation for each Reporting Unit/Entity'
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Public comparables',
      'A list of public competitors and public comparable firms for each Reporting Unit/Entity'
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Financials - Historical, last 2-3 years',
      'Consolidated historical income statements, balance sheets, and cash flow statements for each Reporting Unit/Entity',
      { description: 'for the past two to three fiscal years; preferably in Excel' }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Financials - LTM',
      'Last twelve-month (LTM) income statement, balance sheet, and cash flow statement for each Reporting Unit/Entity',
      {
        description:
          'as of the Measurement Date, with all amounts allocated from Corporate (both income statement and balance sheet); preferably in Excel',
      }
    ),
    questionItem(
      COMPANY_DOCUMENTS,
      'Balance sheet items not assumed',
      'In a hypothetical sale of the Reporting Unit/Entity, would any balance sheet items not be assumed by a market participant?',
      { description: 'such as intercompany accounts or related party loans, as applicable' }
    ),
    questionItem(
      COMPANY_DOCUMENTS,
      'Book value as proxy for fair value?',
      'Confirm book value is a reasonable proxy for fair value for all asset and liability line items on the LTM balance sheet',
      { description: 'if not, please identify those line items' }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Controlling projections',
      'Financial P&L projections for each Reporting Unit/Entity (controlling, market participant basis)',
      {
        description:
          'at least three years as of the Measurement Date, base case/most realistic scenario, detailing COGS, SG&A (including stock-based compensation), R&D, depreciation, amortization, and capital expenditures; if available, base, optimistic, and pessimistic cases with probabilities and projected balance sheets; preferably in Excel',
      }
    ),
    questionItem(
      COMPANY_DOCUMENTS,
      'Non-controlling projections',
      'How would the projections change if the market participant was a non-controlling buyer?',
      {
        description:
          'e.g., could we assume the same revenue growth and gross margin with operating expenses increased by X%? If so, please provide this percentage; we can also discuss on a call',
      }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Fixed assets tax life',
      'Tax life on existing fixed assets and new assets for each Reporting Unit/Entity'
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Actuals vs. budget',
      'Comparison of income statement actuals versus budget for the current fiscal year end ("FYE") for each Reporting Unit/Entity',
      { description: 'if available; please briefly explain any major variances' }
    ),

    questionItem(
      TRANSACTION_STRUCTURE,
      'Stock or asset deal?',
      'Was the initial transaction structured as a stock deal or an asset deal for tax purposes?',
      { description: 'and can we assume another market participant would structure the transaction the same way?' }
    ),
    documentItem(
      TRANSACTION_STRUCTURE,
      'Tax amortization schedule (stock deal)',
      'Future amortization schedule on a tax basis by fiscal year end for each Reporting Unit/Entity',
      {
        description:
          'detailed by definite-lived intangible asset subject to amortization until the end of the remaining useful life; only applicable in a stock deal',
      }
    ),
    questionItem(
      TRANSACTION_STRUCTURE,
      'NOL balance (stock deal)',
      'Outstanding federal net operating loss (NOL) balance as of the Measurement Date for each Reporting Unit/Entity',
      { description: 'pre- and post-2018 NOL balances; only applicable in a stock deal' }
    ),
    questionItem(
      TRANSACTION_STRUCTURE,
      'Shares outstanding (public)',
      'Shares outstanding as of the Measurement Date',
      {
        description: 'public companies only',
      }
    ),
    questionItem(
      TRANSACTION_STRUCTURE,
      'Cost of debt',
      'Cost of debt on interest-bearing debt as of the Measurement Date for each Reporting Unit/Entity',
      { description: 'as applicable' }
    ),
  ],
};
